import React, { useState } from "react";
import { Box, Button, Chip, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { PersonRef } from "../types";
import PeoplePicker from "./PeoplePicker";
import { ButtonTertiary } from "src/UI/ButtonTertiary";

interface PeopleFieldProps {
  label: string;
  required?: boolean;
  value: PersonRef[];
  emptyLabel: string;
  pickerTitle: string;
  corporateId: string | number;
  roleFilter: string;
  error?: boolean;
  onChange: (people: PersonRef[]) => void;
}

const PeopleField: React.FC<PeopleFieldProps> = ({
  label,
  required,
  value,
  emptyLabel,
  pickerTitle,
  corporateId,
  roleFilter,
  error,
  onChange,
}) => {
  const { t } = useTranslation("session-wizard");
  const theme = useTheme();
  const [pickerOpen, setPickerOpen] = useState(false);

  const handleRemove = (id: string | number) => {
    onChange(value.filter((p) => String(p.id) !== String(id)));
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            color: theme.palette.text.primary,
          }}
        >
          {label}
          {required ? " *" : ""}
        </Typography>
        <ButtonTertiary
          endIcon={<Plus size={"1rem"} />}
          onClick={() => setPickerOpen(true)}
        >
          {t("actions.add")}
        </ButtonTertiary>
      </Box>

      {value.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 2,
            fontSize: "0.875rem",
            color: theme.palette.textDisabled,
            borderRadius: "0.5rem",
            backgroundColor: theme.palette.backgroundPrimaryControl,
          }}
        >
          {emptyLabel}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            padding: "10px",
            borderRadius: "0.5rem",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {value.map((p) => (
            <Chip
              key={String(p.id)}
              label={p.label}
              onDelete={() => handleRemove(p.id)}
              sx={{ borderRadius: "8px", fontWeight: 600 }}
            />
          ))}
        </Box>
      )}

      {pickerOpen && (
        <PeoplePicker
          title={pickerTitle}
          corporateId={corporateId}
          roleFilter={roleFilter}
          initialSelected={value}
          onClose={() => setPickerOpen(false)}
          onConfirm={onChange}
        />
      )}
    </Box>
  );
};

export default PeopleField;
